import { createHttpClient } from '@vista-digital/http-client';
import { OrderCompletionStatus, OrderPaymentStatus } from '@vista-digital/ocapi-types/v1';
import { loadScripts } from '../utils/load-script';
import { logger } from '../utils/logger';
import { validateParams } from '../utils/validate-params';
import { PaymentFailedError } from './types/payment-failed-error';
import { PaymentFailedErrorResultCode } from './types/payment-failed-error-result-code';
export class PaymentClient {
    async initialiseConnector() {
        // Check if there's an existing payment. No need to continue if the payment has already been completed/failed.
        const existingPaymentDetails = await this.getExistingPaymentDetails();
        if (existingPaymentDetails) return this.handleExistingPaymentDetails(existingPaymentDetails);
        const { container , events  } = this.params;
        const { clientData  } = await this.createEmbeddedPayment();
        const { sdkScriptUrls , connectorScriptUrls , connectorFunctionName , connectorParametersJson , completePaymentUrl , isPaymentButtonIncluded  } = clientData;
        this.isPaymentButtonIncluded = isPaymentButtonIncluded;
        await loadScripts(sdkScriptUrls.concat(connectorScriptUrls));
        if (!window.vista.payments.connector[connectorFunctionName]) {
            throw new Error(`Failed to initialise connector. Function name '${connectorFunctionName}' not found.`);
        }
        const serverParams = JSON.parse(connectorParametersJson);
        container.innerHTML = '';
        const clientParams = {
            container: container,
            onPaymentTokenReceived: (token)=>this.handleTokenReceived(completePaymentUrl, token)
            ,
            onError: (errorMessage)=>this.handleConnectorError(errorMessage)
            ,
            onPaymentMethodDeclined: (errorMessage)=>this.handlePaymentDeclined(errorMessage)
            ,
            onPaymentButtonStateChanged: (state)=>events.onPaymentButtonStateChanged(state)
            ,
            onPaymentStarted: ()=>events.onPaymentStarted()
        };
        this.paymentGatewayUI = window.vista.payments.connector[connectorFunctionName](serverParams, clientParams);
    }
    unmount() {
        if (this.paymentGatewayUI && typeof this.paymentGatewayUI.unmount === 'function') {
            this.paymentGatewayUI.unmount();
        }
        this.params.container.innerHTML = '';
    }
    constructor(params){
        this.startPayment = ()=>{
            var ref;
            this.params.events.onPaymentButtonStateChanged({
                isDisabled: true
            });
            (ref = this.paymentGatewayUI) == null ? void 0 : ref.startPayment();
        };
        this.getExistingPaymentDetails = async ()=>{
            const { hasPaymentStarted  } = this.params;
            // No need to check if we have been explicitly told that the payment has not started.
            if (hasPaymentStarted === false) return undefined;
            const orderCompletion = await this.getOrderCompletion();
            // We're expecting the order to be completed after a redirect.
            if (hasPaymentStarted && !orderCompletion) return {
                isSuccessful: false
            };
            // Order completion has started.
            if (orderCompletion) {
                // Handle the completion status.
                if (orderCompletion.status === OrderCompletionStatus.Completed) {
                    return {
                        isSuccessful: true,
                        orderCompletion: orderCompletion
                    };
                } else {
                    return {
                        isSuccessful: false
                    };
                }
            }
            return undefined;
        };
        this.handleExistingPaymentDetails = async (details)=>{
            if (details.isSuccessful) {
                this.params.events.onPaymentSuccessful(details.orderCompletion);
            } else {
                this.handlePaymentFailed(PaymentFailedErrorResultCode.OrderCompletionFailed);
            }
        };
        this.createEmbeddedPayment = async ()=>{
            const { ocapiClient , orderId , webPaymentMethodId , redirectReturnUrl , languageTag  } = this.params;
            try {
                return await ocapiClient.v1.orders.payments.embedded.create(orderId, {
                    webPaymentMethodId,
                    redirectReturnUrl,
                    languageTag
                });
            } catch (e) {
                throw new PaymentFailedError(PaymentFailedErrorResultCode.ApiError);
            }
        };
        this.getCurrentEmbeddedPayment = async ()=>{
            const { ocapiClient , orderId  } = this.params;
            try {
                return await ocapiClient.v1.orders.payments.embedded.getCurrent(orderId);
            } catch (e) {
                throw new PaymentFailedError(PaymentFailedErrorResultCode.ApiError);
            }
        };
        this.getOrderCompletion = async ()=>{
            const { ocapiClient , orderId  } = this.params;
            try {
                return await ocapiClient.v1.orders.completion.get(orderId);
            } catch (e) {
                this.handlePaymentFailed(PaymentFailedErrorResultCode.ApiError);
                throw new PaymentFailedError(PaymentFailedErrorResultCode.ApiError);
            }
        };
        this.handlePaymentAttempt = async ()=>{
            const payment = await this.getCurrentEmbeddedPayment();
            if (payment.status === OrderPaymentStatus.Completed) return this.handlePaymentSuccessful();
            this.handlePaymentFailed(PaymentFailedErrorResultCode.PaymentDeclined);
        };
        this.handlePaymentSuccessful = async ()=>{
            const orderCompletion = await this.getOrderCompletion();
            if (orderCompletion && orderCompletion.status === OrderCompletionStatus.Completed) {
                return this.params.events.onPaymentSuccessful(orderCompletion);
            }
            this.handlePaymentFailed(PaymentFailedErrorResultCode.OrderCompletionFailed);
        };
        this.handlePaymentFailed = (resultCode)=>{
            this.params.events.onPaymentFailed(new PaymentFailedError(resultCode));
        };
        this.handleTokenReceived = async (completePaymentUrl, token)=>{
            const httpClient = createHttpClient({
                headers: new Headers({
                    'Content-Type': 'application/x-www-form-urlencoded'
                }),
                maxAttempts: 1
            });
            try {
                const response = await httpClient.post(completePaymentUrl, token, {
                    parseResponse: false
                });
                if (response.status === 302) {
                    // redirection
                    response.text().then((result)=>document.write(result)
                    );
                } else {
                    this.handlePaymentAttempt();
                }
            } catch (e) {
                this.handlePaymentFailed(PaymentFailedErrorResultCode.ApiError);
                throw new PaymentFailedError(PaymentFailedErrorResultCode.ApiError);
            }
        };
        this.handleConnectorError = (errorMessage)=>{
            logger.error(errorMessage);
            this.handlePaymentFailed(PaymentFailedErrorResultCode.ConnectorError);
            throw new PaymentFailedError(PaymentFailedErrorResultCode.ConnectorError);
        };
        this.handlePaymentDeclined = (errorMessage)=>{
            logger.error(errorMessage);
            this.handlePaymentFailed(PaymentFailedErrorResultCode.PaymentDeclined);
            throw new PaymentFailedError(PaymentFailedErrorResultCode.PaymentDeclined);
        };
        const requiredParams = [
            'orderId',
            'webPaymentMethodId',
            'container',
            'childContainerClassName',
            'events',
            'ocapiClient'
        ];
        const valid = validateParams(params, requiredParams);
        if (!valid) throw new Error('Failed to create PaymentClient. Missing required params.');
        this.params = params;
    }
}

//# sourceMappingURL=payment-client.js.map